import React from 'react';
import axios from 'axios';

let localStorageSupported;

export function canUseLocalStorage() {
    if (localStorageSupported !== undefined) {
        return localStorageSupported;
    }

    if (typeof window === 'undefined') {
        localStorageSupported = false;
        return localStorageSupported;
    }

    try {
        const storage = window.localStorage;
        const testKey = '__storage_test__';
        storage.setItem(testKey, testKey);
        storage.removeItem(testKey);
        localStorageSupported = true;
    } catch (err) {
        console.warn('Local storage is not available.', err);
        localStorageSupported = false;
    }

    return localStorageSupported;
}

export function getLocalStorageItem(key) {
    if (!canUseLocalStorage()) return null;
    try {
        return window.localStorage.getItem(key);
    } catch (err) {
        console.warn('Failed to read from localStorage', err);
        return null;
    }
}

export function setLocalStorageItem(key, value) {
    if (!canUseLocalStorage()) return false;
    try {
        window.localStorage.setItem(key, value);
        return true;
    } catch (err) {
        console.warn('Failed to write to localStorage', err);
        return false;
    }
}

export function removeLocalStorageItem(key) {
    if (!canUseLocalStorage()) return;
    try {
        window.localStorage.removeItem(key);
    } catch (err) {
        console.warn('Failed to remove item from localStorage', err);
    }
}


// Simple ROT13 encoder/decoder
export function rot13(str) {
    return str.replace(/[a-zA-Z]/g, function(c) {
        return String.fromCharCode(
            (c <= 'Z' ? 90 : 122) >= (c = c.charCodeAt(0) + 13) ? c : c - 26
        );
    });
}

// Save the user's email encoded with ROT13
export function saveEmailToLocalStorage(email) {
    const encoded = rot13(email);
    if (!setLocalStorageItem('userEmail', encoded)) {
        console.error('Failed to store email');
    }
}

// Retrieve and decode the stored email
export function getEmailFromLocalStorage() {
    const encoded = getLocalStorageItem('userEmail');
    return encoded ? rot13(encoded) : null;
}


export class ErrorBoundary extends React.Component {
    constructor(props) {
      super(props);
      this.state = { hasError: false, error: "" };
    }

    componentDidCatch(error, errorInfo) {
      postData('/api/log-error', {
        type: `ReactErrorBoundary ${this.props.version}`,
        message: error.toString(),
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      }).catch((err) => {
        console.error("Error logging to server:", err);
        throw err
      });
      this.setState({ hasError: true, error: error.toString() });
    }

    render() {
      if (this.state.hasError) {
        return <h1>Something went wrong. {this.state.error}</h1>;
      }
      return this.props.children;
    }
}

export async function fetchData(url, config) {
    try {
        if (config) {
            if (config.headers || config.signal) {
                const response = await axios.get(url, config);
                return response.data;
            } else {
                const response = await axios.get(url, { headers: config });
                return response.data;
            }
        } else {
            const response = await axios.get(url);
            return response.data;
        }
    } catch (error) {
        console.error('GET request failed:', error);
        throw error;
    }
}

export async function postData(url, data, config = {}) {
    try {
        // Attach email for log-error endpoint if not already provided
        if (url.includes('/api/log-error')) {
            const email = data && data.email ? data.email : getEmailFromLocalStorage();
            if (email) {
                data = { ...data, email };
            }
        }

        const response = await axios.post(url, data, config);
        return response.data;
    } catch (error) {
        if (error.code === 'ECONNABORTED') {
            console.error('Request timed out');
        } else {
            console.error('Something else went wrong', error);
            throw error;
        }
    }
}
